/** Usage reporting and querying (spec section 17 "Costs with centrally assigned keys"). */
import { z } from 'zod';
import { AccountIdSchema, DeviceIdSchema, OperationIdSchema, UserIdSchema } from '../ids.js';
import { UsageVerificationStatusSchema } from '../providers/records.js';
import { CountSchema, CursorQuerySchema, MicroUsdSchema, SemVerSchema, TimeRangeQuerySchema, defineRoute, pageOf } from './common.js';
import { USAGE_OUTCOMES, UsageRecordSchema, UsageServiceSchema } from './entities.js';

export const UsageReportRequestSchema = z
  .object({
    deviceId: DeviceIdSchema,
    records: z
      .array(
        UsageRecordSchema.omit({ userId: true, receivedAt: true, reconciledAt: true, actualCostMicroUsd: true, aiKeyId: true }).strict(),
      )
      .min(1)
      .max(200),
  })
  .strict();
export type UsageReportRequest = z.infer<typeof UsageReportRequestSchema>;

export const UsageReportResponseSchema = z.object({
  accepted: z.array(z.string()),
  duplicates: z.array(z.string()),
  /** Budget after settling the reported usage. */
  budgetRemainingMicroUsd: MicroUsdSchema.nullable(),
});

export const UsageQuerySchema = CursorQuerySchema.extend(TimeRangeQuerySchema.shape).extend({
  userId: UserIdSchema.optional(),
  accountId: AccountIdSchema.optional(),
  deviceId: DeviceIdSchema.optional(),
  service: UsageServiceSchema.optional(),
  model: z.string().max(128).optional(),
  operationId: OperationIdSchema.optional(),
  verificationStatus: UsageVerificationStatusSchema.optional(),
  // Spec section 13 "Usage and audit views": filter by app version and status (outcome).
  outcome: z.enum(USAGE_OUTCOMES).optional(),
  appVersion: SemVerSchema.optional(),
});
export type UsageQuery = z.infer<typeof UsageQuerySchema>;

export const UsageSummarySchema = z.object({
  requestCount: CountSchema,
  inputTokens: CountSchema,
  outputTokens: CountSchema,
  cachedTokens: CountSchema,
  reasoningTokens: CountSchema,
  estimatedCostMicroUsd: MicroUsdSchema,
  /** Null when no reconciled records exist (display "unknown", not zero). */
  actualCostMicroUsd: MicroUsdSchema.nullable(),
  reconciledCount: CountSchema,
  estimatedCount: CountSchema,
  unknownCount: CountSchema,
  byModel: z.array(
    z.object({ model: z.string(), requestCount: CountSchema, estimatedCostMicroUsd: MicroUsdSchema, inputTokens: CountSchema, outputTokens: CountSchema }),
  ),
});
export type UsageSummary = z.infer<typeof UsageSummarySchema>;

export const UsagePageSchema = pageOf(UsageRecordSchema).extend({ summary: UsageSummarySchema });
export type UsagePage = z.infer<typeof UsagePageSchema>;

export const usageRoutes = {
  'usage.report': defineRoute({
    id: 'usage.report',
    method: 'POST',
    path: '/v1/usage',
    auth: 'desktop',
    requestSchema: UsageReportRequestSchema,
    responseSchema: UsageReportResponseSchema,
    description: 'Report device-observed usage (estimated). Idempotent on record id.',
  }),
  'usage.query': defineRoute({
    id: 'usage.query',
    method: 'GET',
    path: '/v1/usage',
    auth: 'desktop',
    requestSchema: UsageQuerySchema,
    responseSchema: UsagePageSchema,
    description: "Caller's usage with account/service/time filters and a summary.",
  }),
} as const;
